import { isValid, isAfter, addDays } from 'date-fns';

export type ShouldExcludeUserConfig = {
	/**
	 * The date to check against (e.g., account creation date, first active date)
	 */
	checkDate: Date | null;
	/**
	 * The number of days to exclude users whose date is within the last N days. Defaults to 14 days.
	 */
	daysThreshold?: number;
	/**
	 * When provided, users whose date is AFTER this date are excluded
	 * (these are new users who don't require changeboarding).
	 */
	cutoffDate?: Date;
};

/**
 * Determines if a user should be excluded based on date-based criteria.
 *
 * @param config Configuration object containing the date to check and exclusion criteria
 * @returns Object containing exclusion result and computed values
 */
export const useShouldExcludeUser = ({
	checkDate,
	daysThreshold = 14,
	cutoffDate,
}: ShouldExcludeUserConfig):
	| {
			shouldExclude: boolean;
			isNewlyCreatedUser: null;
			isNewUserAfterCutoffDate: boolean;
	  }
	| {
			shouldExclude: boolean;
			isNewlyCreatedUser: boolean;
			isNewUserAfterCutoffDate: boolean;
	  } => {
	// If there is no check date, we cannot determine if user is new
	if (!checkDate) {
		return {
			shouldExclude: true,
			isNewlyCreatedUser: null,
			isNewUserAfterCutoffDate: false,
		};
	}

	const isNewlyCreatedUser = isAfter(addDays(checkDate, daysThreshold), new Date());

	// Check if user is a new user (date is AFTER the cutoff date)
	// These users don't need changeboarding since it's all new to them
	const isNewUserAfterCutoffDate =
		cutoffDate && isValid(cutoffDate) ? isAfter(checkDate, cutoffDate) : false;

	const shouldExclude = isNewlyCreatedUser || isNewUserAfterCutoffDate;

	return {
		shouldExclude,
		isNewlyCreatedUser,
		isNewUserAfterCutoffDate,
	};
};
